import React, {
  useMemo,
  useCallback,
  forwardRef,
  useImperativeHandle,
  useEffect,
  useState,
  useRef,
} from "react";
import { useForm, useApi, useModal } from "hooks";
import { FormMode, Path } from "enums";
import { initialState as formState } from "./station-details.state";
import StationDetailsFormModule from "./station-details-form.module";
import styles from "./station-details.module.scss";
import locale from "localization";
import { Loader } from "components/commons";
import { handleRequest } from "utils";
import { ConfirmModal } from "components/modals";
import { useHistory } from "react-router-dom";
import { createStation, updateStation, getStations } from "apis/station.api";
import { stationFormPost, stationFormGet } from "./station-form.utils";
import { capitalizeAll } from "utils/text.utils";
import { DEFAULT_PRODUCTS, ALL_PRODUCT_CODES } from "./station-constants";

const StationDetailsModule = ({ pageMode, stationData, stationCount, ...props }, ref) => {
  const { setUpdateLoading, setDisableUpdate } = props;
  const viewMode = pageMode === FormMode.View;
  const addMode = pageMode === FormMode.Add;
  const editMode = pageMode === FormMode.Edit;

  const [formValid, setFormValid] = useState(false);
  const [isValidCoordinates, setIsValidCoordinates] = useState(false);
  const [products, setProducts] = useState(DEFAULT_PRODUCTS);
  const initialProductsRef = useRef(null);
  const initialLocationRef = useRef(null);
  const [locationChanged, setLocationChanged] = useState(false);

  const { request: getStationsRequest, loading: loadingStations } = useApi({
    api: getStations,
    pageError: true,
  });

  const form = useMemo(() => {
    if (!stationData || Object.keys(stationData).length === 0) return formState({});
    return formState(stationFormGet(stationData));
  }, [stationData]);

  useEffect(() => {
    if (!stationData || Object.keys(stationData).length === 0) return;
    if (stationData.stationProduct) {
      const rawProducts = Array.isArray(stationData.stationProduct)
        ? stationData.stationProduct
        : Object.keys(stationData.stationProduct).filter((k) => stationData.stationProduct[k] === true);
      const existingProducts = ALL_PRODUCT_CODES.filter((code) => rawProducts.includes(code));
      if (existingProducts.length > 0) {
        setProducts(existingProducts);
        if (initialProductsRef.current === null) {
          initialProductsRef.current = [...existingProducts];
        }
      }
    }
    if (stationData.latitude != null && stationData.longitude != null) {
      setIsValidCoordinates(true);
    }
  }, [stationData]);

  const confirmModal = useModal();
  const { show, close } = confirmModal;
  const history = useHistory();

  const {
    fields,
    modifyField,
    isFormSubmittable,
    submitForm,
    modifyForm,
    clearForm,
    getFormValues,
    applyFieldErrors,
  } = useForm({ initialState: form });

  // Capture initial location once after form loads in edit mode
  useEffect(() => {
    if (editMode && initialLocationRef.current === null && fields?.stationLocation?.value) {
      initialLocationRef.current = fields.stationLocation.value;
    }
  }, [editMode, fields?.stationLocation?.value]);

  // Detect location changes
  useEffect(() => {
    if (
      editMode &&
      initialLocationRef.current !== null &&
      fields?.stationLocation?.value &&
      fields.stationLocation.value !== initialLocationRef.current &&
      !locationChanged
    ) {
      setLocationChanged(true);
    }
  }, [editMode, fields?.stationLocation?.value, locationChanged]);

  const formAttributeValidation = useCallback(() => {
    const v = getFormValues();

    const hasProductsChanged = editMode
      ? initialProductsRef.current !== null &&
        JSON.stringify([...products].sort()) !== JSON.stringify([...initialProductsRef.current].sort())
      : true;

    const basicInfoValid = editMode
      ? hasProductsChanged || locationChanged
      : (v.stationCode?.length === 4 &&
          v.stationName?.length >= 5 &&
          v.businessName?.length >= 5 &&
          v.customerCode?.length >= 4 &&
          !!v.stationType &&
          !!v.island &&
          !!v.province);

    return basicInfoValid && products.length >= 3 && !!v.stationLocation && isValidCoordinates;
  }, [editMode, getFormValues, products, isValidCoordinates, locationChanged]);

  const addRequest = useApi({
    api: createStation,
    handleOwnError: { badrequest: true },
  });

  const editRequest = useApi({
    api: updateStation,
    handleOwnError: { badrequest: true },
    params: { stationId: stationData?.stationId },
  });

  const loading = addRequest.loading || editRequest.loading;

  const submit = (params) => {
    const apiRequest = addMode ? addRequest : editRequest;
    handleRequest(
      async () => {
        close();
        await apiRequest.request({ ...params }, () => handleSubmit());
        clearForm();
        if (addMode) {
          show({
            title: locale.exclamatedSuccess,
            content: (
              <locale.Populate
                text={locale.successfullyAddStation}
                items={[<b>{capitalizeAll(params.name)}</b>]}
              />
            ),
            secondary: {
              text: locale.goBackStationList,
              onClick: () => history.push(Path.StationList),
            },
            primary: {
              text: locale.addNewSeaOilStation,
              onClick: () => {
                setProducts(DEFAULT_PRODUCTS);
                close();
              },
            },
          });
        } else {
          await getStationsRequest();
          show({
            title: locale.exclamatedSuccess,
            content: locale.detailsHaveBeenUpdated,
            primary: { text: locale.gotIt, onClick: () => history.push(Path.StationList) },
          });
        }
      },
      {
        S1000: () => {
          applyFieldErrors({ stationCode: locale.stationAlreadyExist });
          window.scrollTo({ top: 0, behavior: "smooth" });
        },
      }
    );
  };

  const handleSubmit = () => {
    if (!formAttributeValidation()) return;
    const currentFormValues = getFormValues();
    const payload = stationFormPost(currentFormValues, products);
    show({
      title: addMode ? locale.addNewSeaOilStationQuestion : locale.saveChangesQuestion,
      content: addMode ? (
        <>
          <locale.Populate
            text={locale.youAreAboutToAddStation}
            items={[<b>{capitalizeAll(payload.name)}</b>]}
          />
          <br />
          <locale.Populate text={locale.doYouWantToProceedQuestion} items={[]} />
        </>
      ) : (
        locale.areYouSureYouWantToSaveStation
      ),
      secondary: {
        text: addMode ? locale.Cancel : locale.continueEditing,
      },
      primary: {
        text: addMode ? locale.addSeaOilStation : locale.saveChanges,
        onClick: () => submit(payload),
      },
    });
  };

  useImperativeHandle(ref, () => ({
    handleUpdate() {
      submitForm(handleSubmit);
    },
  }));

  useEffect(() => {
    if (editMode) {
      setUpdateLoading(loading);
      setDisableUpdate(!formAttributeValidation());
    }
  }, [editMode, loading, setDisableUpdate, setUpdateLoading, formAttributeValidation]);

  useEffect(() => {
    if (!editMode) {
      setFormValid(isFormSubmittable === true && formAttributeValidation());
    }
  }, [editMode, isFormSubmittable, formAttributeValidation]);

  const detailsFormProps = {
    viewMode,
    addMode,
    editMode,
    fields,
    isFormSubmittable,
    loading,
    modifyField,
    submitForm,
    modifyForm,
    handleSubmit,
    formAttributeValidation,
    getStationsRequest,
    applyFieldErrors,
    formValid,
    getFormValues,
    setIsValidCoordinates,
    products,
    setProducts,
  };

  return (
    <div>
      <Loader open={loadingStations} />
      {!loadingStations && (
        <div className={styles.container}>
          {!viewMode && <ConfirmModal {...confirmModal} />}
          <StationDetailsFormModule {...detailsFormProps} />
        </div>
      )}
    </div>
  );
};

export default forwardRef(StationDetailsModule);
